import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail } from "lucide-react";
import calmistyleLogo from "@/assets/calmistyle-logo.png";
import shopPayLogo from "@/assets/shop-pay-logo.webp";

const footerLinks = {
  shop: [
    { name: "Shop", href: "/" },
    { name: "Collections", href: "/collections" },
    { name: "Customer Reviews", href: "/reviews" },
  ],
  company: [
    { name: "Our Story", href: "/about" },
    { name: "Transparency", href: "/transparency" },
    { name: "Compare", href: "/compare" },
    { name: "Blog", href: "/blog" },
  ],
  support: [
    { name: "FAQ", href: "/faq" },
    { name: "Contact", href: "/contact" },
    { name: "Shipping", href: "/shipping" },
    { name: "Returns & Refunds", href: "/returns" },
    { name: "Order Tracking", href: "/tracking" },
  ],
  legal: [
    { name: "Terms & Conditions", href: "/terms" },
    { name: "Privacy Policy", href: "/privacy" },
  ],
};

export const Footer = () => {
  return (
    <footer className="bg-secondary border-t border-border">
      <div className="container-calim section-padding">
        {/* Main Footer Content */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-8">
          {/* Brand Column */}
          <div className="col-span-2 lg:col-span-2">
            <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <img 
              src={calmistyleLogo} 
              alt="CALMISTYLE" 
              width={80}
              height={80}
              className="h-16 md:h-20 w-auto"
            />
              <span className="font-display text-xl md:text-2xl font-light tracking-wide">
                CALMISTYLE
              </span>
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6 max-w-sm">
              Wellness accessories inspired by traditional acupressure.
              Your daily ritual for calm and serenity.
            </p>
            {/* Social Links */}
            <div className="flex items-center space-x-4">
              <a
                href="https://www.instagram.com/calmistyle/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61586647700316"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.tiktok.com/@calmistyle"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="TikTok"
              >
                {/* TikTok Icon */}
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              <a
                href="mailto:contact@calmistyle.com"
                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Email"
              >
                <Mail className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Links Columns */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-foreground mb-4">Shop</p>
            <ul className="space-y-3">
              {footerLinks.shop.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-foreground mb-4">Company</p>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-foreground mb-4">Support</p>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="mt-12 pt-8 border-t border-border/50">
          <div className="flex flex-wrap justify-center items-center gap-4 mb-6">
            {/* Visa */}
            <div className="h-10 px-3 bg-white rounded-md flex items-center justify-center border border-border/30 shadow-sm">
              <svg viewBox="0 0 780 500" className="h-6 w-auto">
                <path fill="#1434CB" d="M293.2 348.7l33.4-195.7h53.4L346.5 348.7H293.2zM584.9 157.2c-10.6-4-27.2-8.3-47.9-8.3-52.8 0-90 26.5-90.3 64.5-.3 28.1 26.5 43.8 46.8 53.1 20.8 9.5 27.8 15.6 27.7 24.1-.1 13-16.6 19-32 19-21.4 0-32.8-3-50.4-10.3l-6.9-3.1-7.5 43.8c12.5 5.5 35.7 10.2 59.7 10.4 56.2 0 92.6-26.2 93.1-66.8.2-22.3-14-39.2-44.8-53.2-18.7-9.1-30.1-15.1-30-24.3 0-8.1 9.7-16.8 30.6-16.8 17.5-.3 30.1 3.5 40 7.5l4.8 2.3 7.1-41.9zM687.4 153h-41.4c-12.8 0-22.4 3.5-28 16.3L535 348.7h56.2s9.2-24.1 11.3-29.4l68.5.1c1.6 6.8 6.5 29.3 6.5 29.3h49.7L687.4 153zm-66.1 127.7c4.4-11.2 21.4-54.5 21.4-54.5-.3.5 4.4-11.4 7.1-18.8l3.6 17s10.3 46.9 12.4 56.3H621.3zM240.8 153l-52.3 133-5.6-27c-9.7-31.3-40-65.3-73.9-82.3l47.8 171.9h56.6L297.5 153H240.8z"/>
                <path fill="#F9A533" d="M124.5 153H40.3l-.7 4c67.2 16.2 111.7 55.4 130.2 102.4l-18.8-90c-3.2-12.3-12.6-16-26.5-16.4z"/>
              </svg>
            </div>
            {/* Mastercard */}
            <div className="h-10 px-3 bg-white rounded-md flex items-center justify-center border border-border/30 shadow-sm">
              <svg viewBox="0 0 780 500" className="h-6 w-auto">
                <circle fill="#EB001B" cx="310" cy="250" r="130"/>
                <circle fill="#F79E1B" cx="470" cy="250" r="130"/>
                <path fill="#FF5F00" d="M390 147c32.4 26.4 53.1 66.7 53.1 111.8s-20.7 85.4-53.1 111.8c-32.4-26.4-53.1-66.7-53.1-111.8s20.7-85.4 53.1-111.8z"/>
              </svg>
            </div>
            {/* PayPal */}
            <div className="h-10 px-3 bg-white rounded-md flex items-center justify-center border border-border/30 shadow-sm">
              <svg viewBox="0 0 780 500" className="h-6 w-auto">
                <path fill="#003087" d="M168.4 167.6h-91c-6.2 0-11.5 4.5-12.5 10.7l-36.9 234c-.7 4.6 2.8 8.8 7.4 8.8h43.5c6.2 0 11.5-4.5 12.5-10.7l10-63.2c1-6.2 6.3-10.7 12.5-10.7h28.8c60 0 94.6-29 103.7-86.5 4.1-25.2.2-45-11.5-58.8-12.9-15.1-35.7-23.6-67.5-23.6zm10.5 85.2c-5 32.7-30 32.7-54.2 32.7h-13.8l9.7-61.2c.6-3.7 3.7-6.4 7.4-6.4h6.3c16.5 0 32 0 40 9.4 4.8 5.6 6.2 14 4.6 25.5z"/>
                <path fill="#009CDE" d="M526 167.6h-91c-6.2 0-11.5 4.5-12.5 10.7l-36.9 234c-.7 4.6 2.8 8.8 7.4 8.8h46.8c4.3 0 8-3.2 8.7-7.5l10.5-66.4c1-6.2 6.3-10.7 12.5-10.7h28.8c60 0 94.6-29 103.7-86.5 4.1-25.2.2-45-11.5-58.8-12.9-15.1-35.7-23.6-67.5-23.6zm10.5 85.2c-5 32.7-30 32.7-54.2 32.7h-13.8l9.7-61.2c.6-3.7 3.7-6.4 7.4-6.4h6.3c16.5 0 32 0 40 9.4 4.8 5.6 6.2 14 4.6 25.5z"/>
              </svg>
            </div>
            {/* Shop Pay */}
            <div className="h-10 px-3 bg-white rounded-md flex items-center justify-center border border-border/30 shadow-sm">
              <img src={shopPayLogo} alt="Shop Pay" width={66} height={24} className="h-6 w-auto" />
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 border-t border-border/50">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} CALMISTYLE. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              {footerLinks.legal.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-4 text-center md:text-left">
            CALMISTYLE is a wellness accessory and does not constitute a medical device.
            Results may vary from person to person.
          </p>
        </div>
      </div>
    </footer>
  );
};